import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './select-amenities.module.scss';
import { store } from 'react-notifications-component';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { AmenityUiModel } from '../../../../../shared/ui-models/amenity.ui-model';
import { AmenityIcon } from '../../../../../shared/components/icons/amenities-icons';

export interface SelectAmenitiesProps {
  data: {
    selectedAmenities: AmenityUiModel[];
    amenitiesList: AmenityUiModel[];
  };
  config: {
    isLoading: boolean;
  };
  eventHandlers: {
    selectAmenity: (amenity: AmenityUiModel) => void;
    unselectAmenity: (amenityId: string) => void;
  };
}

/**
 * functional component SelectAmenities
 * @param {SelectAmenitiesProps} holding question text
 */
export const SelectAmenities: React.FC<SelectAmenitiesProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const { t } = useTranslation();
  const [isAmenitiesExpanded, setIsAmenitiesExpanded] = useState<boolean>(true);

  useEffect(() => {
    return () => {
      store.removeNotification('assign-unit-type-failed');
      store.removeNotification('assign-unit-type-success');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        onClick={() => setIsAmenitiesExpanded(!isAmenitiesExpanded)}
        style={{
          cursor: 'pointer',
          padding: '1.5rem 2rem',
          backgroundColor: isAmenitiesExpanded ? '#237c58' : '#f0f7f4',
          borderRadius: '8px',
          margin: '1.5rem 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.3s ease',
          boxShadow: isAmenitiesExpanded
            ? '0 4px 12px rgba(35, 124, 88, 0.2)'
            : '0 2px 6px rgba(35, 124, 88, 0.1)'
        }}
      >
        <div
          className={styles['select-amenities-container__sub-title']}
          style={{ margin: 0, color: isAmenitiesExpanded ? '#fff' : '#237c58' }}
        >
          {t('PROPERTY_MODULE.SELECT_AMENITIES_CONTAINER.TITLE')}
        </div>
        <span
          style={{
            fontSize: '1.4rem',
            color: isAmenitiesExpanded ? '#fff' : '#237c58',
            transition: 'transform 0.4s ease',
            transform: isAmenitiesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
            fontWeight: 'bold'
          }}
        >
          ▼
        </span>
      </div>

      {isAmenitiesExpanded && (
        <div
          style={{
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          <style>
            {`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>

          <div className={styles['select-amenities-container']}>
            {config.isLoading ? (
              <div className={styles['select-amenities-container__cards-container__loader']}>
                <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
              </div>
            ) : (
              <>
                {t('PROPERTY_MODULE.SELECT_AMENITIES_CONTAINER.SUB_TITLE')}
                <div className={styles['select-amenities-container__cards-container']}>
                  {data.amenitiesList && data.amenitiesList.length > 0 ? (
                    data.amenitiesList.map((amenity) => (
                      <div
                        key={amenity.id}
                        className={styles['select-amenities-container__cards-container__card']}
                        onClick={() => {
                          const selected =
                            data.selectedAmenities.findIndex((item) => item.id === amenity.id) > -1;
                          if (selected) {
                            if (amenity && amenity.id) {
                              eventHandlers.unselectAmenity(amenity.id);
                            }
                          } else {
                            eventHandlers.selectAmenity(amenity);
                          }
                        }}
                      >
                        <div
                          className={
                            styles['select-amenities-container__cards-container__card__checkbox']
                          }
                        >
                          {data.selectedAmenities.findIndex((item) => item.id === amenity.id) >
                          -1 ? (
                            <CheckCircleIcon fontSize={'inherit'} color={'inherit'} />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize={'inherit'} color={'inherit'} />
                          )}
                        </div>
                        <div
                          className={
                            styles[
                              'select-amenities-container__cards-container__card__image-container'
                            ]
                          }
                        >
                          <AmenityIcon
                            amenityName={amenity.name}
                            className={
                              styles[
                                'select-amenities-container__cards-container__card__image-container__image'
                              ]
                            }
                            aria-label={amenity.name}
                          />
                        </div>
                        <div
                          className={
                            styles['select-amenities-container__cards-container__card__name']
                          }
                        >
                          {amenity.name}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>{t('PROPERTY_MODULE.SELECT_AMENITIES_CONTAINER.NO_AMENITIES')}</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SelectAmenities;
